import { get, handleError, isHttpNotFound, post, put } from '@/lib/http-adapter';
import type { GiftCardSpend, Order, ReceiptGuest } from '@/lib/types';
import type { MetaAttribution } from '@/services/tracking';
import type { AcceptedTerms } from '@/services/terms';

// Every scheduler order create answers with this one shape, whatever the type.
export type CreatedOrder = {
  order_id: string;
  // The guest's only proof the order is theirs until Stripe returns a session id.
  receipt_token: string;
  // Null when the order needs no Stripe page: nothing is left to pay.
  checkout_url: string | null;
  // Only on a class, pack or subscription order a money gift card pays
  // toward (gift cards M1); the session charges exactly `remainder`, the
  // first payment's on a subscription.
  gift_card?: GiftCardSpend;
};

interface CreateClassPackOrderPayload {
  class_pack_items: {
    class_pack_id: string;
    quantity: number;
    original_price: number;
  }[];
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  coupon_code?: string | null;
  coupon_type?: 'STRIPE' | 'CUSTOM';
  discount_percentage?: number | null;
  class_pack_code_id?: string | null;
  meta_attribution?: MetaAttribution;
  accepted_terms?: AcceptedTerms[];
  // Its own field, never coupon_code, and never beside a discount (D31, M1).
  gift_card_code?: string;
  // The kept gifted order this create replaces, with the receipt token its
  // create answered (gift cards G27, G38). Never on a gift card purchase.
  replaces_order_id?: string;
  receipt_token?: string;
}

export const createClassPackOrder = async (
  payload: CreateClassPackOrderPayload
): Promise<CreatedOrder | undefined> => {
  try {
    return await post<CreatedOrder>('/public/orders/class-pack', {
      ...payload,
    });
  } catch (error) {
    handleError(error);
  }
};

export interface GuestInput {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country_code: string;
  accepted_terms?: AcceptedTerms[];
}

interface CreateClassOrderPayload {
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  coupon_code?: string | null;
  coupon_type?: 'STRIPE' | 'CUSTOM';
  discount_percentage?: number | null;
  discount_amount?: number | null;
  // Its own field, never coupon_code, and never beside a discount (D31).
  gift_card_code?: string;
  original_price: number;
  total_amount: number;
  final_amount: number;
  quantity: number;
  class_id: string;
  instructor_schedule_id: string;
  slot_id: string | null;
  override_slot_id?: string | null;
  timezone: string;
  start_time: string;
  end_time: string;
  meta_attribution?: MetaAttribution;
  accepted_terms?: AcceptedTerms[];
  guests?: GuestInput[];
  // The kept gifted order this create replaces, with the receipt token its
  // create answered (gift cards G27, G38). Never on a gift card purchase.
  replaces_order_id?: string;
  receipt_token?: string;
}

export const createClassOrder = async (
  payload: CreateClassOrderPayload
): Promise<CreatedOrder | undefined> => {
  try {
    // The order refuses a credit or guests without a session before it writes
    // anything (createClassOrderForScheduler), and an access cookie can lapse
    // while the customer fills in the form.
    return await post<CreatedOrder>(
      '/public/orders/class',
      { ...payload },
      { retryOnLoginRequired: true }
    );
  } catch (error) {
    handleError(error);
  }
};

export interface CreateSubscriptionOrderPayload {
  subscription_id: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  coupon_code?: string | null;
  coupon_type?: 'STRIPE' | 'CUSTOM';
  discount_percentage?: number | null;
  discount_amount?: number | null;
  stripe_promotion_code_id?: string | null;
  meta_attribution?: MetaAttribution;
  accepted_terms?: AcceptedTerms[];
  switch_from_subscription_code_id?: string;
  // The first payment only, never on a plan switch (D306).
  gift_card_code?: string;
  // The kept gifted order this create replaces, with the receipt token its
  // create answered (gift cards G27, G38). Never on a gift card purchase.
  replaces_order_id?: string;
  receipt_token?: string;
}

export const createSubscriptionOrder = async (
  payload: CreateSubscriptionOrderPayload
): Promise<CreatedOrder | undefined> => {
  try {
    return await post<CreatedOrder>('/public/orders/subscription', {
      ...payload,
    });
  } catch (error) {
    handleError(error);
  }
};

// Who the gift is for. The email is left out on a send-to-self card; send_on is
// a calendar day in the studio's zone, or null for now.
export type GiftCardRecipient = {
  name: string;
  email?: string;
  message: string | null;
  send_to_self: boolean;
  send_on: string | null;
};

// No coupon, discount or gift card key, not even empty: the route refuses any
// of them with GIFT_CARD_NO_DISCOUNT, so the pack body builder is not reused.
export interface CreateGiftCardOrderPayload {
  gift_card_id: string;
  // A stale-cart signal only; the price comes from the gift card row.
  original_price: number;
  recipient: GiftCardRecipient;
  user_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  country_code?: string;
  meta_attribution?: MetaAttribution;
  accepted_terms?: AcceptedTerms[];
}

export const createGiftCardOrder = async (
  payload: CreateGiftCardOrderPayload
): Promise<CreatedOrder | undefined> => {
  try {
    return await post<CreatedOrder>('/public/orders/gift-card', payload);
  } catch (error) {
    handleError(error);
  }
};

interface BookingPrecheckPayload {
  email: string;
  slot_id: string | null;
  override_slot_id?: string | null;
  start_time: string;
  class_id?: string;
  instructor_schedule_id?: string;
  timezone?: string;
}

export type BookingBlockReason =
  | 'CLASS_ALREADY_STARTED'
  | 'BOOKING_RULE_VIOLATION';

// bookable=false always ships a reason and message, bookable=true never does;
// encoding the pairing lets consumers read them without null checks.
export type BookingPrecheckResult = {
  already_booked: boolean;
  booked_spots: number;
} & (
  | { bookable: true; block_reason: null; block_message: null }
  | { bookable: false; block_reason: BookingBlockReason; block_message: string }
);

export const checkBookingPrecheck = async (
  payload: BookingPrecheckPayload
): Promise<BookingPrecheckResult | undefined> => {
  try {
    return await post<BookingPrecheckResult>(
      '/public/orders/booking-precheck',
      payload
    );
  } catch {
    // Advisory check: fail open so a precheck outage never blocks booking.
    // Capture already happened in the interceptor.
    return undefined;
  }
};

// receipt_token stands in for identity when there is no Stripe session_id:
// free orders and confirmation-email links, which guests must still be able to
// open without an account.
export interface OrderReceiptParams {
  session_id?: string;
  order_id?: string;
  receipt_token?: string;
}

export const getClassPackOrderDetails = async (params: OrderReceiptParams) => {
  try {
    const response = await get<{
      order: Order;
    }>('/public/orders/class-pack', {
      params,
    });

    return response.order;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const getClassOrderDetails = async (params: OrderReceiptParams) => {
  try {
    const response = await get<{
      order: Order;
    }>('/public/orders/class', {
      params,
    });

    return response.order;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const getSubscriptionOrderDetails = async (
  params: OrderReceiptParams
) => {
  try {
    const response = await get<{
      order: Order;
    }>('/public/orders/subscription', { params });

    return response.order;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const getTopupOrderDetails = async (params: OrderReceiptParams) => {
  try {
    const response = await get<{
      order: Order;
    }>('/public/orders/topup', { params });

    return response.order;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const getGiftCardOrderDetails = async (params: OrderReceiptParams) => {
  try {
    const response = await get<{
      order: Order;
    }>('/public/orders/gift-card', { params });

    return response.order;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

// What Stripe actually refunded, in major units. It is 0 both when nothing was
// due and when the refund itself failed, which the wire cannot tell apart.
// The guest fields are absent from a backend before guest passes.
type CancelClassResult = {
  refunded_amount: number;
  // Only when a money gift card paid toward the order: its share of the
  // refund, already back on the card.
  gift_card_refunded_amount?: number;
  cancelled_guest_order_ids?: string[];
  guest_passes_returned?: number;
};

export const cancelClass = async (
  orderId: string,
  // At most one of from_slot_id / from_override_slot_id — scopes the cancel
  // to the booked item's occurrence; from_start_time completes the key, since
  // a weekly slot id alone also matches the order's items on other dates.
  payload?: {
    quantity?: number;
    from_slot_id?: string;
    from_override_slot_id?: string;
    from_start_time?: string;
    // A host's order: also cancel the guests at the class times touched.
    cancel_guests?: boolean;
  }
) => {
  try {
    // The response interceptor unwraps to the body, so the resolved type is the
    // second generic; axios's default AxiosResponse<T> never reaches a caller.
    return await put<CancelClassResult, CancelClassResult>(
      `/public/orders/${encodeURIComponent(orderId)}/cancel`,
      payload ?? {}
    );
  } catch (error: any) {
    handleError(error);
  }
};

interface RescheduleClassPayload {
  slot_id: string | null;
  override_slot_id?: string | null;
  instructor_schedule_id: string;
  start_time: string;
  end_time: string;
  timezone: string;
  quantity?: number;
  // At most one of the pair — source occurrence the moved spots come from;
  // required for multi-slot orders so the backend targets the right items.
  // from_start_time completes the key for a weekly slot booked on two dates.
  from_slot_id?: string;
  from_override_slot_id?: string;
  from_start_time?: string;
}

export const rescheduleClass = async (
  orderId: string,
  payload: RescheduleClassPayload
) => {
  try {
    return await put(
      `/public/orders/${encodeURIComponent(orderId)}/reschedule`,
      payload
    );
  } catch (error: any) {
    handleError(error);
  }
};

type AddOrderGuestsResult = {
  guests: ReceiptGuest[];
  passes_left: number;
};

// Guests join the host's own class time, paid by a pass on the code that paid
// the host's order.
export const addOrderGuests = async (
  orderId: string,
  payload: {
    slot_id?: string | null;
    override_slot_id?: string | null;
    start_time: string;
    guests: GuestInput[];
  }
) => {
  try {
    return (await post(
      `/public/orders/${encodeURIComponent(orderId)}/guests`,
      payload
    )) as AddOrderGuestsResult;
  } catch (error: any) {
    handleError(error);
  }
};

type RemoveOrderGuestResult = {
  guest_order_id: string;
  pass_returned: boolean;
};

export const removeOrderGuest = async (
  orderId: string,
  guestOrderId: string
) => {
  try {
    return await put<RemoveOrderGuestResult, RemoveOrderGuestResult>(
      `/public/orders/${encodeURIComponent(orderId)}/guests/${encodeURIComponent(guestOrderId)}/cancel`,
      {}
    );
  } catch (error: any) {
    handleError(error);
  }
};
